import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PssCharacterDesign, PssCrewMember, PssInventoryItem, PssItemDesign } from '../../../services/pss-auth.service';
import { spriteIdOrEmpty } from '../pss-display-utils';
import { PssSpriteComponent } from '../../pss-sprite/pss-sprite.component';

@Component({
  selector: 'app-crew-flyout',
  standalone: true,
  imports: [CommonModule, PssSpriteComponent],
  templateUrl: './crew-flyout.component.html',
  styleUrls: ['../dashboard.component.css']
})
export class CrewFlyoutComponent {
  @Input() crew: PssCrewMember | null = null;
  @Input() characterDesigns = new Map<string, PssCharacterDesign>();
  @Input() itemDesigns = new Map<string, PssItemDesign>();
  @Input() inventory: PssInventoryItem[] = [];

  @Output() close = new EventEmitter<void>();

  private static readonly CREW_STAT_KEY_TO_CHARACTER_FIELD: Partial<Record<keyof PssCrewMember, keyof PssCharacterDesign>> = {
    weaponImp: 'finalWeapon',
    attackImp: 'finalAttack',
    scienceImp: 'finalScience',
    engineImp: 'finalEngine',
    pilotImp: 'finalPilot',
    repairImp: 'finalRepair',
    abilityImp: 'specialAbilityArg',
    hpImp: 'finalHp',
  };

  characterName(c: PssCrewMember): string {
    return c.customName || this.characterDesigns.get(c.designId)?.name || `#${c.designId}`;
  }

  crewName(c: PssCrewMember): string {
    return this.characterName(c);
  }

  crewRarity(c: PssCrewMember): string {
    return this.characterDesigns.get(c.designId)?.rarity || 'common';
  }

  crewSpriteId(c: PssCrewMember): string {
    return spriteIdOrEmpty(this.characterDesigns.get(c.designId)?.profileSpriteId);
  }

  crewBaseStat(c: PssCrewMember, stat: keyof PssCrewMember): number {
    const field = CrewFlyoutComponent.CREW_STAT_KEY_TO_CHARACTER_FIELD[stat];
    if (!field) return 0;
    const d = this.characterDesigns.get(c.designId);
    if (!d) return 0;
    const value = d[field] as string | undefined;
    return parseFloat(value || '0') || 0;
  }

  crewTrainingPct(c: PssCrewMember, stat: keyof PssCrewMember): number {
    return parseInt((c[stat] as string) || '0', 10) || 0;
  }

  crewTotalStat(c: PssCrewMember, stat: keyof PssCrewMember): number {
    const base = this.crewBaseStat(c, stat);
    const training = this.crewTrainingPct(c, stat);
    return Number.isFinite(base) ? base * (1 + training / 100) : 0;
  }

  equipmentList(c: PssCrewMember): Array<{ instanceId: string; designId: string }> {
    const slots: Array<{ instanceId: string; designId: string }> = [];
    if (c.equipItemIds?.length) {
      for (const iid of c.equipItemIds) {
        const inv = this.inventory.find(item => item.equippedItemIds?.includes(iid));
        if (inv) {
          slots.push({ instanceId: iid, designId: inv.designId });
        }
      }
    }
    if (!slots.length && c.equipDesignIds?.length) {
      for (const d of c.equipDesignIds) {
        slots.push({ instanceId: '', designId: d });
      }
    }
    return slots;
  }
}
